// Shared helpers for the EditListing wizard panels.
//
// Originally lived inside EditListingDetailsPanel.js when DETAILS was
// the wizard's only panel. Stream 1 PR-4 moved them here so the
// panel could be deleted; nothing in the wizard depends on a single
// "details" coordinator anymore.

// Default scheduling values applied to existing kit listings that
// have no `publicData.scheduling` block yet (default-on-read; no
// backfill) AND to brand-new draft listings before the host saves
// the form for the first time. The same defaults are referenced by
// the customer-side date picker so an edited-but-unsaved listing
// renders consistently.
export const SCHEDULING_DEFAULTS_KIT = {
  leadTime: '48h',
  multipleBookingsPerDay: false,
  timeBetweenBookings: 'none',
};

// Pricing mode. Stored as publicData.pricingMode.
//   'fixed' — vendor sets per-tier prices (Basic/Deluxe). Listing
//     uses the standard event-kit-rental-v2 booking process. Default
//     for any listing without publicData.pricingMode (default-on-read
//     for existing listings; no backfill).
//   'quote' — vendor sets a startingPrice and (optionally) a list of
//     pre-fill items. Listing uses default-negotiation: customer
//     requests a quote, vendor responds with an offer.
//
// IMMUTABLE AFTER FIRST SAVE. Sharetribe ties the transaction
// process to the listing at the API level — switching mid-life
// would break any active or historical transactions. KIT_TYPE step
// gates the card selector once publicData.pricingMode is set.
export const PRICING_MODES = ['fixed', 'quote'];

export const PRICING_MODE_TO_PROCESS_ALIAS = {
  fixed: 'event-kit-rental-v2/release-2',
  quote: 'default-negotiation/release-1',
};

export const PRICING_MODE_TO_UNIT_TYPE = {
  fixed: 'night',
  quote: 'offer',
};

/**
 * Resolve the canonical pricingMode for a save-time form payload.
 * Defaults to 'fixed' for any unrecognised / missing value so a
 * stale draft never silently flips a listing into negotiation mode.
 */
export const resolvePricingMode = raw =>
  PRICING_MODES.includes(raw) ? raw : 'fixed';

// Shared "starting price" unit vocabulary — the SINGLE SOURCE OF TRUTH
// for both the kit Base-pricing step (publicData.startingPriceUnit) and
// the add-on rows (publicData.addOnCategories[].pricingUnit). Both are
// "starting from" floors, so they speak one language; do not reintroduce
// a separate add-on list. The STORED value is the structured basis; the
// display label is DERIVED so the two can't drift. 'flat' = a per-booking
// floor (label "per booking"); 'per_hour' supports hourly kits/add-ons
// (DJ, photographer). The kit field is deliberately NOT publicData
// .unitType (locked to the booking process 'night'). No quote-total calc
// consumes the basis yet — stored structured for when the engine lands.
export const STARTING_PRICE_UNITS = [
  { basis: 'flat', label: 'per booking' },
  { basis: 'per_day', label: 'per day' },
  { basis: 'per_person', label: 'per person' },
  { basis: 'per_item', label: 'per item' },
  { basis: 'per_hour', label: 'per hour' },
];

export const DEFAULT_STARTING_PRICE_UNIT = 'flat';

// Back-compat aliases for legacy tokens (pre-unification). The add-on
// step used 'per_guest' before adopting this shared vocabulary; map it
// to 'per_person' so any stray stored row still resolves + renders. No
// migration script needed.
const STARTING_PRICE_UNIT_ALIASES = { per_guest: 'per_person' };

// Validate a raw basis (applying aliases) against the known set; fall
// back to the default so a stale draft / future value never renders a
// blank unit.
export const resolveStartingPriceUnit = raw => {
  const aliased = STARTING_PRICE_UNIT_ALIASES[raw] || raw;
  return STARTING_PRICE_UNITS.some(u => u.basis === aliased)
    ? aliased
    : DEFAULT_STARTING_PRICE_UNIT;
};

// Display label for a stored basis token (e.g. 'flat' -> 'per booking').
export const startingPriceUnitLabel = raw =>
  (
    STARTING_PRICE_UNITS.find(u => u.basis === resolveStartingPriceUnit(raw)) ||
    {}
  ).label || '';

// ─── Canonical price quantifier (SINGLE SOURCE OF TRUTH for display) ───
//
// Every surface that prints the price unit next to a listing's price —
// the search card, OrderPanel, the mobile booking bar, the search-map
// card, the host's manage-listing card — must derive its label HERE so
// the host's chosen pricing model ports identically everywhere.
//
// - Quote-mode kits store the host's chosen basis in
//   publicData.startingPriceUnit -> use it.
// - Fixed/tiered kits price each tier per day (tiers[*].pricePerDay), so
//   their basis is 'per_day'.
//
// NEVER derive the label from the booking-process publicData.unitType
// (which defaults to 'night'): that's what leaked "/night" onto kits
// that aren't priced by night. unitType is a transaction-process detail,
// not the listing's display unit.
export const listingUnitBasis = (publicData = {}) =>
  publicData?.pricingMode === 'quote'
    ? resolveStartingPriceUnit(publicData?.startingPriceUnit)
    : 'per_day';

// Canonical display label (e.g. "per day", "per person") for a listing's
// publicData. The one function every price surface should call.
export const listingUnitLabel = (publicData = {}) =>
  startingPriceUnitLabel(listingUnitBasis(publicData));

// Default id generator for the quote-sheet normalisers
// (normaliseQuoteSheet / mergeQuoteSheet). Random suffix is
// fine here — these ids are stable React keys for the form's
// repeating rows, not anything that needs to be reproducible across
// sessions. Tests inject a deterministic generator via the second
// argument to keep assertions clean.
export const defaultItemIdGenerator = () =>
  `item-${Math.random()
    .toString(36)
    .slice(2, 10)}`;

// ─── Quote sheet (host's private priced worksheet) ───
//
// The quote sheet lives in privateData.quoteSheet and is *derived*
// from the kit contents (publicData.packageItems) so the host prices
// items they already entered instead of retyping them. Each row is a
// plain invoice line — quantity × per-unit price:
//
//   { id, fromKit, sourceId, name, quantity, unitPrice }
//
//   - id        stable React key + identity (carried across re-seeds)
//   - fromKit   true = seeded from a kit item; false = host-added line
//   - sourceId  the packageItems[].id this row binds to (null until the
//               kit item has an id, or always-null for host-added rows)
//   - quantity  host-entered integer ≥ 1
//   - unitPrice integer CENTS, per unit (0 = unpriced; Stage 3 enforces
//               pricing at send). Line total = quantity × unitPrice.
//
// No per-row unit type: a quote is a host-hand-built custom price, so
// quantity + per-unit price is the natural model (the dropdown and the
// quantity column were doing overlapping jobs). The separate
// starting-price unit selector is unrelated and stays. Shape is
// line-item-ready so Stage 3 maps each row straight to a native
// line-item/offer (quantity × unitPrice → unitPrice/quantity), no
// unit-type translation. Money is cents to match Sharetribe Money.
const QUOTE_SHEET_MAX = 30;

// Case-insensitive, trimmed name key used for the merge name-fallback.
const normQuoteName = s => (typeof s === 'string' ? s.trim().toLowerCase() : '');

// Quote-sheet row source. Back-compat: rows stored before this change
// have `fromKit` (bool) and no `source` — quoteRowSource() bridges them.
const VALID_QUOTE_SOURCES = ['kit', 'addon', 'manual'];
const quoteRowSource = row =>
  VALID_QUOTE_SOURCES.includes(row?.source)
    ? row.source
    : row?.fromKit === true
    ? 'kit'
    : 'manual';

// DISPLAY-ONLY quantity hint that drives the form placeholder ("Enter
// guest count" / "Enter hours"). It NEVER enters the offer math — the
// host types the real quantity for the specific quote. null = generic
// "Qty for this quote". A per-guest kit item and a per_person add-on
// both hint 'per_person'; a per_hour add-on hints 'per_hour'.
const qtyHintFromKit = k => (k?.quantity === 'per-guest' ? 'per_person' : null);
const qtyHintFromAddonUnit = unit =>
  unit === 'per_person' ? 'per_person' : unit === 'per_hour' ? 'per_hour' : null;

// Build a fresh quote-sheet row from a kit item. Quantity seeds BLANK
// (null) — EVERY row (kit + add-on) is quantity-blank so the host states
// each quantity for this specific quote, removing the silent-default /
// single-unit undercount risk. Name carries; kit items seed unpriced.
const seedRowFromKit = (k, idGenerator) => ({
  id: idGenerator(),
  source: 'kit',
  fromKit: true, // back-compat for any un-migrated reader
  sourceId: typeof k?.id === 'string' && k.id ? k.id : null,
  name: typeof k?.name === 'string' ? k.name.trim() : '',
  quantity: null,
  unitPrice: 0,
  qtyHint: qtyHintFromKit(k),
});

// Build a fresh quote-sheet row from an add-on (stored shape:
// { category, startingAt cents, pricingUnit, ... }). Carries the add-on's
// startingAt price (the host already set it; they can adjust). Quantity
// blank; tagged source 'addon'.
const seedRowFromAddon = (a, idGenerator, label) => ({
  id: idGenerator(),
  source: 'addon',
  fromKit: false,
  sourceId: typeof a?.category === 'string' && a.category ? a.category : null,
  name: label || '',
  quantity: null,
  unitPrice: Number.isInteger(a?.startingAt) && a.startingAt > 0 ? a.startingAt : 0,
  qtyHint: qtyHintFromAddonUnit(a?.pricingUnit),
});

/**
 * Normalise raw quote-sheet rows (form-side: dollars-string unitPrice,
 * string quantity, possibly incomplete) into the canonical stored
 * shape (integer cents, stable id). Plain quantity × unitPrice lines.
 *
 * @param {Array} rowsRaw form values (values.pub_items)
 * @param {() => string} [idGenerator] injectable for tests
 * @returns {Array} rows ready to write to privateData.quoteSheet
 */
export const normaliseQuoteSheet = (
  rowsRaw,
  idGenerator = defaultItemIdGenerator
) => {
  if (!Array.isArray(rowsRaw)) return [];
  return rowsRaw
    .map(row => {
      const name = typeof row?.name === 'string' ? row.name.trim() : '';
      if (!name) return null;
      // Blank/invalid quantity stores as null (NOT 1) — the quote sheet
      // is quantity-blank by design; the host enters the real number at
      // quote time, and the send-gate requires it then.
      const qtyParsed = parseInt(row?.quantity, 10);
      const quantity = Number.isInteger(qtyParsed) && qtyParsed > 0 ? qtyParsed : null;
      const priceParsed = parseFloat(row?.unitPrice);
      const unitPrice =
        Number.isFinite(priceParsed) && priceParsed > 0
          ? Math.round(priceParsed * 100)
          : 0;
      const source = quoteRowSource(row);
      const sourceId =
        typeof row?.sourceId === 'string' && row.sourceId ? row.sourceId : null;
      const qtyHint =
        row?.qtyHint === 'per_person' || row?.qtyHint === 'per_hour' ? row.qtyHint : null;
      const isDraftId =
        typeof row?.id === 'string' && row.id.startsWith('tmp-');
      const id = !row?.id || isDraftId ? idGenerator() : row.id;
      return { id, source, fromKit: source === 'kit', sourceId, name, quantity, unitPrice, qtyHint };
    })
    .filter(Boolean)
    .slice(0, QUOTE_SHEET_MAX);
};

/**
 * Reconcile an existing quote sheet against the current kit contents AND
 * add-ons. Pure. Returns { rows, dropped } — rows is the reconciled sheet
 * (canonical stored shape), dropped is [{ name }] for kit/add-on-sourced
 * rows whose source is gone (so the panel can surface a non-silent notice).
 *
 * Three sources, reconciled in order (kit, then add-ons), keyed on
 * sourceId so edits to either source pick up adds/removes without
 * clobbering host-entered quantities/prices:
 *   - kit   : sourceId === packageItems[].id (id match → name fallback → seed)
 *   - addon : sourceId === addOnCategories[].category (seed carries startingAt)
 *   - manual: host-added rows (source 'manual'), always kept + appended
 * On match: preserve host quantity (null = blank) + unitPrice; refresh name
 * from the source. Add-on price seeds from startingAt on FIRST create only —
 * the host's value always wins thereafter (never re-clobbered).
 *
 * @param {Array} storedSheet privateData.quoteSheet (stored shape)
 * @param {Array} packageItems publicData.packageItems ({ id?, name, quantity })
 * @param {Array} [addOnCategories] publicData.addOnCategories (stored shape)
 * @param {() => string} [idGenerator] injectable for tests
 * @returns {{ rows: Array, dropped: Array<{name:string}> }}
 */
export const mergeQuoteSheet = (
  storedSheet,
  packageItems,
  addOnCategories = [],
  idGenerator = defaultItemIdGenerator
) => {
  const { findAddonCategory } = lazyAddonModule();
  const sheet = Array.isArray(storedSheet) ? storedSheet : [];
  const kit = Array.isArray(packageItems) ? packageItems : [];
  const addOns = Array.isArray(addOnCategories) ? addOnCategories : [];
  const kitIds = new Set(
    kit.map(k => (typeof k?.id === 'string' && k.id ? k.id : null)).filter(Boolean)
  );
  const claimed = new Set();
  const rows = [];

  // Preserve a host-entered quantity (positive int) else blank (null);
  // preserve a host-entered price else 0.
  const keepQty = s => (Number.isInteger(s?.quantity) && s.quantity > 0 ? s.quantity : null);
  const keepPrice = s => (Number.isInteger(s?.unitPrice) && s.unitPrice >= 0 ? s.unitPrice : 0);

  // 1. KIT items
  kit.forEach(k => {
    const name = typeof k?.name === 'string' ? k.name.trim() : '';
    if (!name) return; // skip blank kit rows
    const kid = typeof k?.id === 'string' && k.id ? k.id : null;

    // 1a. id match
    let idx =
      kid != null
        ? sheet.findIndex(
            (s, i) => !claimed.has(i) && quoteRowSource(s) === 'kit' && s.sourceId === kid
          )
        : -1;
    // 1b. name fallback (null or stale sourceId only)
    if (idx === -1) {
      const target = normQuoteName(name);
      idx = sheet.findIndex(
        (s, i) =>
          !claimed.has(i) &&
          quoteRowSource(s) === 'kit' &&
          normQuoteName(s.name) === target &&
          (s.sourceId == null || !kitIds.has(s.sourceId))
      );
    }

    if (idx !== -1) {
      claimed.add(idx);
      const s = sheet[idx];
      rows.push({
        id: s.id || idGenerator(),
        source: 'kit',
        fromKit: true,
        sourceId: kid != null ? kid : typeof s.sourceId === 'string' ? s.sourceId : null,
        name, // refresh from kit so renames propagate to the label
        quantity: keepQty(s),
        unitPrice: keepPrice(s),
        qtyHint: qtyHintFromKit(k),
      });
    } else {
      rows.push(seedRowFromKit({ ...k, name }, idGenerator));
    }
  });

  // 2. ADD-ONS
  addOns.forEach(a => {
    const cat = typeof a?.category === 'string' && a.category ? a.category : null;
    if (!cat) return;
    const label = findAddonCategory(cat)?.label || cat;

    const idx = sheet.findIndex(
      (s, i) => !claimed.has(i) && quoteRowSource(s) === 'addon' && s.sourceId === cat
    );
    if (idx !== -1) {
      claimed.add(idx);
      const s = sheet[idx];
      rows.push({
        id: s.id || idGenerator(),
        source: 'addon',
        fromKit: false,
        sourceId: cat,
        name: label, // refresh from catalog
        quantity: keepQty(s),
        unitPrice: keepPrice(s), // host price wins; never re-clobber with startingAt
        qtyHint: qtyHintFromAddonUnit(a?.pricingUnit),
      });
    } else {
      rows.push(seedRowFromAddon(a, idGenerator, label));
    }
  });

  // 3. Unclaimed rows: kit/add-on whose source is gone → drop + report;
  //    manual (host-added) rows are always kept + appended.
  const dropped = [];
  sheet.forEach((s, i) => {
    if (claimed.has(i)) return;
    const src = quoteRowSource(s);
    if (src === 'kit' || src === 'addon') {
      dropped.push({ name: typeof s?.name === 'string' ? s.name : '' });
    } else {
      rows.push({
        id: s?.id || idGenerator(),
        source: 'manual',
        fromKit: false,
        sourceId: null,
        name: typeof s?.name === 'string' ? s.name : '',
        quantity: keepQty(s),
        unitPrice: keepPrice(s),
        qtyHint: null,
      });
    }
  });

  return { rows: rows.slice(0, QUOTE_SHEET_MAX), dropped };
};

// ─── Add-on category normalisation (Stream 3 PR-1) ───
//
// Form-side rows arrive with dollars-string prices and string lead
// times; the catalog defines the canonical credential flags
// (requiresLicense / requiresCOI). The normaliser stamps the
// catalog flags onto each row at save time so listing-side data
// can't drift from the source of truth, parses prices to integer
// cents, validates the pricingUnit / cancellationPolicy / category
// against their respective enums, and caps the array.
//
// Imports are lazy-required to avoid a circular dep between
// editListingHelpers and the data modules.

const lazyAddonModule = () => require('../data/addon-categories');
const lazyCancellationModule = () => require('../data/cancellation-policies');

// ─── Address privacy split (2026-07-02, owner decision) ───
//
// The wizard used to store the host's EXACT street address in
// publicData.location — publicly readable via the Sharetribe API and
// serialized into page source. The exact address now lives in listing
// privateData; publicData keeps a CITY-LEVEL string ("Goleta,
// California 93117, United States") that display labels and ZIP range
// checks still work from. FAIL CLOSED: if no US state segment is
// recognizable, publicAddress is null — never guess and leak a street.
const lazySearchModule = () => require('./search');
export const splitAddressForPrivacy = fullAddress => {
  const full = typeof fullAddress === 'string' ? fullAddress.trim() : '';
  if (!full) return { publicAddress: null, privateAddress: null };
  const { US_STATE_ABBR } = lazySearchModule();
  const parts = full.split(',').map(t => t.trim()).filter(Boolean);
  let i = parts.length - 1;
  let country = null;
  if (/^united states/i.test(parts[i] || '')) {
    country = parts[i];
    i -= 1;
  }
  const stateZip = parts[i] || '';
  const stateName = stateZip.replace(/\d{5}(-\d{4})?/, '').trim();
  const known =
    !!US_STATE_ABBR[stateName] || Object.values(US_STATE_ABBR).includes(stateName);
  if (!known || i < 1) return { publicAddress: null, privateAddress: full };
  const city = parts[i - 1];
  const publicAddress = [city, stateZip, country].filter(Boolean).join(', ');
  return { publicAddress, privateAddress: full };
};


/**
 * Normalise an add-on rows array (form-side shape) into the
 * canonical stored shape.
 *
 * Rules:
 *   - drop rows with an unknown / missing category key (a stale
 *     draft or hand-edited payload could reference a removed
 *     category — fail it loud, not silently)
 *   - startingAt: parse dollars (string or number) → integer
 *     cents. 0 / NaN / negative → 0
 *   - pricingUnit: resolved via the shared STARTING_PRICE_UNITS vocab
 *     (resolveStartingPriceUnit) — aliases legacy 'per_guest' to
 *     'per_person', falls back to 'flat'
 *   - description: trimmed string (may be empty)
 *   - minLeadTimeDays: parse to integer; fall back to the catalog
 *     default (7); enforce floor (1)
 *   - cancellationPolicy: validated against the four
 *     CANCELLATION_POLICY_KEYS; fall back to 'moderate'
 *   - requiresLicense / requiresCOI: stamped from the catalog,
 *     ignoring whatever the row had — catalog is the source of
 *     truth (locked decision in Stream 3 design doc §2)
 *   - cap at MAX_ADDONS_PER_LISTING (20)
 *
 * @param {Array} rowsRaw  form values
 * @returns {Array} normalised rows, ready to write to
 *                  publicData.addOnCategories
 */
export const normaliseAddOnCategories = rowsRaw => {
  const {
    findAddonCategory,
    ADDON_ROW_DEFAULTS,
    MIN_LEAD_TIME_DAYS_FLOOR,
    MAX_ADDONS_PER_LISTING,
  } = lazyAddonModule();
  const { CANCELLATION_POLICY_KEYS } = lazyCancellationModule();

  if (!Array.isArray(rowsRaw)) return [];
  return rowsRaw
    .map(row => {
      const cat = findAddonCategory(row?.category);
      if (!cat) return null;

      const priceParsed = parseFloat(row?.startingAt);
      const startingAt =
        Number.isFinite(priceParsed) && priceParsed > 0
          ? Math.round(priceParsed * 100)
          : 0;

      // Add-ons use the shared STARTING_PRICE_UNITS vocabulary (single
      // source of truth). resolveStartingPriceUnit applies the
      // per_guest->per_person back-compat alias + falls back to 'flat'.
      const pricingUnit = resolveStartingPriceUnit(row?.pricingUnit);

      const description =
        typeof row?.description === 'string' ? row.description.trim() : '';

      // minLeadTimeDays is required at entry (form validation); the
      // form no longer pre-fills the old default of 7. A malformed/empty
      // value here (e.g. a stale draft) floors to MIN_LEAD_TIME_DAYS_FLOOR
      // rather than silently injecting 7.
      const leadParsed = parseInt(row?.minLeadTimeDays, 10);
      const minLeadTimeDays = Number.isFinite(leadParsed)
        ? Math.max(MIN_LEAD_TIME_DAYS_FLOOR, leadParsed)
        : MIN_LEAD_TIME_DAYS_FLOOR;

      const cancellationPolicy = CANCELLATION_POLICY_KEYS.includes(
        row?.cancellationPolicy
      )
        ? row.cancellationPolicy
        : ADDON_ROW_DEFAULTS.cancellationPolicy;

      return {
        category: cat.key,
        startingAt,
        pricingUnit,
        description,
        minLeadTimeDays,
        cancellationPolicy,
        // Stamped from the catalog. If catalog flags ever change,
        // they'll re-stamp on the next save — preferred over
        // freezing a stale snapshot on the row.
        requiresLicense: cat.requiresLicense,
        requiresCOI: cat.requiresCOI,
      };
    })
    .filter(Boolean)
    .slice(0, MAX_ADDONS_PER_LISTING);
};

/**
 * Inspect a listing's selected add-ons + the host's credentials and
 * return the list of credential keys the host still needs to verify
 * before publishing. Used by the REVIEW step's Publish gate
 * (locked Q3 — block publish only, with specific credentials named).
 *
 * @param {Array} addOnCategories  publicData.addOnCategories
 * @param {Object} credentials     currentUser publicData.credentials
 * @returns {string[]}  e.g. ['foodServicePermit', 'certificateOfInsurance']
 */
export const missingCredentialsForAddOns = (addOnCategories, credentials) => {
  if (!Array.isArray(addOnCategories) || addOnCategories.length === 0) {
    return [];
  }
  const needsPermit = addOnCategories.some(row => row?.requiresLicense);
  const needsCOI = addOnCategories.some(row => row?.requiresCOI);
  const missing = [];
  const permitStatus = credentials?.foodServicePermit?.status;
  const coiStatus = credentials?.certificateOfInsurance?.status;
  if (needsPermit && permitStatus !== 'verified') {
    missing.push('foodServicePermit');
  }
  if (needsCOI && coiStatus !== 'verified') {
    missing.push('certificateOfInsurance');
  }
  return missing;
};

// Wizard step ids — must match the constants exported by
// EditListingWizardTab. Mirrored here as plain strings to avoid a
// circular import (helpers → wizard tab → helpers).
const STEP_KIT_TYPE = 'kit-type';
const STEP_BASICS = 'basics';
const STEP_PHOTOS = 'photos';
const STEP_INCLUDED = 'included';
const STEP_PRICING_TIERS = 'pricing-tiers';
const STEP_ADDONS = 'addons';
const STEP_LOGISTICS = 'logistics';
const STEP_REVIEW = 'review';

/**
 * Resume tab for the wizard, given a listing's current state.
 *
 * - Published / pending / closed: returns KIT_TYPE so the host can
 *   walk the whole flow again. Stream 3 follow-up (2026-05-15): the
 *   user explicitly asked for "back to the start" on edit of
 *   existing listings, not a deep-link into a specific tab.
 * - Draft: walks the active tab list (which depends on pricingMode)
 *   and returns the first incomplete step. If every step is
 *   complete, returns REVIEW (publish is the only thing left).
 *
 * Completion checks mirror tabCompleted() in EditListingWizard.js
 * verbatim — keep these two in sync. Drift between this helper and
 * the wizard's gating would either route the host to a tab the
 * wizard then bounces them off of, or stick them on a tab they've
 * already completed.
 *
 * @param {Object} listing  Sharetribe own-listing entity
 * @returns {string}        tab id (one of the STEP_* constants)
 */
export const getResumeWizardTab = listing => {
  if (!listing) return STEP_KIT_TYPE;
  const attributes = listing.attributes || {};
  const state = attributes.state;
  if (state && state !== 'draft') return STEP_KIT_TYPE;

  const publicData = attributes.publicData || {};
  const pricingMode = resolvePricingMode(publicData.pricingMode);
  const isQuote = pricingMode === 'quote';

  // Step list per the active wizard flow (matches
  // tabsForListingType in EditListingWizard.js). Custom-quote
  // listings include INCLUDED + ADDONS; fixed mode omits both.
  const steps = isQuote
    ? [
        STEP_KIT_TYPE,
        STEP_BASICS,
        STEP_PHOTOS,
        STEP_INCLUDED,
        STEP_PRICING_TIERS,
        STEP_ADDONS,
        STEP_LOGISTICS,
        STEP_REVIEW,
      ]
    : [
        STEP_KIT_TYPE,
        STEP_BASICS,
        STEP_PHOTOS,
        STEP_PRICING_TIERS,
        STEP_LOGISTICS,
        STEP_REVIEW,
      ];

  // Completion checks — mirror EditListingWizard.js tabCompleted().
  const title = attributes.title;
  const description = attributes.description;
  const price = attributes.price;
  const images = listing.images;
  const isCompleted = step => {
    switch (step) {
      case STEP_KIT_TYPE:
        return !!(
          publicData.pricingMode &&
          publicData.transactionProcessAlias &&
          publicData.unitType
        );
      case STEP_BASICS:
        return !!(title && (description || description === ''));
      case STEP_PHOTOS:
        return Array.isArray(images) && images.length > 0;
      case STEP_INCLUDED:
        // INCLUDED is optional — packageItems can be empty. Treat
        // as complete once PHOTOS has saved (i.e. listing has the
        // listingType + a draft exists with images).
        return !!publicData.listingType;
      case STEP_PRICING_TIERS:
        // Fixed mode requires a positive price (derived from first
        // tier); quote mode also writes the top-level price (from
        // startingPrice). Either way `price` truthy = complete.
        return !!price;
      case STEP_ADDONS:
        return !!price;
      case STEP_LOGISTICS:
        return !!(
          Array.isArray(publicData.fulfillment) &&
          publicData.fulfillment.length > 0 &&
          publicData.location?.address &&
          publicData.scheduling?.leadTime
        );
      case STEP_REVIEW:
      default:
        return false;
    }
  };

  // Walk in order; return the first step that isn't done. If
  // everything's done, return REVIEW so the host publishes.
  for (const step of steps) {
    if (!isCompleted(step)) return step;
  }
  return STEP_REVIEW;
};
